// src/components/Profile/Orders.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Loader from './Loader';
import ErrorMessage from './ErrorMessage';
import ReviewForm from './ReviewForm';

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const token = Cookies.get("ecom_user_token");
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-buyer-ordered-products`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setOrders(response.data.products);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to fetch orders");
        toast.error("Failed to load orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not available';
    const options = { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getCurrentStatus = (order) => {
    if (order.product.status === 'cancelled') {
      if (order.product.isRefunded) {
        return {
          text: 'Refunded',
          color: 'bg-green-100 text-green-800',
          description: `Refund of ₹${order.product.refundedAmount} processed on ${formatDateTime(order.product.refundedAt)}`,
          isRefunded: true
        };
      } else if (order.sellerRefund?.isRefunded) {
        return {
          text: 'Refunded',
          color: 'bg-green-100 text-green-800',
          description: `Refund of ₹${order.sellerRefund.amount} processed on ${formatDateTime(order.sellerRefund.refundedAt)}`,
          isRefunded: true
        };
      }
      return {
        text: 'Cancelled',
        color: 'bg-red-100 text-red-800',
        description: 'This order has been cancelled',
        isRefunded: false
      };
    }

    if (!order.tracking) {
      return {
        text: 'Processing',
        color: 'bg-yellow-100 text-yellow-800',
        description: 'Your order is being prepared',
        isRefunded: false
      };
    }

    switch (order.tracking.status) {
      case 'pending':
        return {
          text: 'Order Placed',
          color: 'bg-blue-100 text-blue-800',
          description: 'Your order has been received',
          isRefunded: false
        };
      case 'confirmed':
        return {
          text: 'Order Confirmed',
          color: 'bg-blue-100 text-blue-800',
          description: 'Seller has accepted your order',
          isRefunded: false
        };
      case 'shipped':
        return {
          text: 'Shipped',
          color: 'bg-blue-100 text-blue-800',
          description: `Your item has been shipped via ${order.tracking.carrier || 'carrier'}`,
          isRefunded: false
        };
      case 'in_transit':
        return {
          text: 'In Transit',
          color: 'bg-purple-100 text-purple-800',
          description: `Your item is on the way via ${order.tracking.carrier || 'carrier'}`,
          isRefunded: false
        };
      case 'delivered':
        return {
          text: 'Delivered',
          color: 'bg-green-100 text-green-800',
          description: 'Your item has been delivered',
          isRefunded: false
        };
      case 'not_shipped':
        return {
          text: 'Processing',
          color: 'bg-yellow-100 text-yellow-800',
          description: 'Seller is preparing your order',
          isRefunded: false
        };
      default:
        return {
          text: 'Processing',
          color: 'bg-yellow-100 text-yellow-800',
          description: 'Your order is being prepared',
          isRefunded: false
        };
    }
  };

  const handleReviewClick = (order) => {
    setSelectedProduct({
      productId: order.product.productId,
      name: order.product.name,
      image: order.product.images[0]
    });
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (reviewData) => {
    try {
      const token = Cookies.get("ecom_user_token");
      const formData = new FormData();
      
      formData.append('ratings', reviewData.rating);
      formData.append('reviews', reviewData.reviewText);
      formData.append('productId', selectedProduct.productId);
      
      reviewData.reviewImages.forEach((image, index) => {
        formData.append('productImages', image);
      });

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/rating/addProductRating`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success("Review submitted successfully!");
      setShowReviewModal(false);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit review");
      return false;
    }
  };

  if (loading) return <Loader fullScreen />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden p-4 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6">My Orders</h2>
      
      {orders.length === 0 ? (
        <div className="text-center py-8 md:py-12 bg-gray-50 rounded-lg">
          <h3 className="mt-2 text-base md:text-lg font-medium text-gray-900">No orders found</h3>
          <p className="mt-1 text-sm md:text-base text-gray-500">You haven't placed any orders yet.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const status = getCurrentStatus(order);
            const expectedDate = order.confirmationDeadline 
              ? `Expected by ${formatDate(order.confirmationDeadline)}` 
              : null;

            return (
              <div 
                key={order.orderItemId} 
                onClick={() => navigate(`/orders/${order.orderItemId}`)}
                className="border rounded-lg p-3 md:p-4 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4">
                  <div className="flex-shrink-0">
                    <img 
                      src={order.product.images[0]} 
                      alt={order.product.name}
                      className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-md"
                    />
                  </div>
                  
                  <div className="flex-grow w-full">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                      <div className="mb-2 sm:mb-0">
                        <h3 className="font-medium text-gray-800 text-base md:text-lg">{order.product.name}</h3>
                        <p className="text-xs md:text-sm text-gray-500">
                          Ordered on {formatDate(order.orderCreatedAt)}
                        </p>
                      </div>
                      <p className="font-medium text-base md:text-lg mb-2 sm:mb-0">₹{order.totalAmount.toFixed(2)}</p>
                    </div>
                    
                    <div className="mt-2 md:mt-3">
                      <div className={`inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium ${status.color}`}>
                        {status.text}
                      </div>
                      <p className="text-xs md:text-sm text-gray-600 mt-1">
                        {status.description}
                        {!status.isRefunded && order.tracking?.carrier && order.product.status !== 'cancelled' && (
                          <span className="block">Carrier: {order.tracking.carrier}</span>
                        )}
                        {!status.isRefunded && expectedDate && order.product.status !== 'cancelled' && (
                          <span className="block">{expectedDate}</span>
                        )}
                      </p>
                    </div>
                    
                    <div className="mt-3 flex justify-end sm:justify-start">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReviewClick(order);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs md:text-sm font-medium py-1 px-2 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                      >
                        Rate & Review
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedProduct && (
        <ReviewForm
          productId={selectedProduct.productId}
          productName={selectedProduct.name}
          productImage={selectedProduct.image}
          onClose={() => setShowReviewModal(false)}
          onReviewSubmit={handleReviewSubmit}
        />
      )}
    </div>
  );
};

export default Orders;